import { useEffect, useState, useRef, useCallback } from "react";
import { BACKEND_URL } from "@/lib/api";
import { CheckCircle2, XCircle, AlertTriangle, Loader2 } from "lucide-react";

const API = `${BACKEND_URL}/api`;

const ACTION_META = {
  mark_done: { verb: "Mark as done", color: "#2E9E6B", Icon: CheckCircle2, past: "marked done" },
  approve: { verb: "Approve", color: "#2E9E6B", Icon: CheckCircle2, past: "approved" },
  reject: { verb: "Reject", color: "#B04A66", Icon: XCircle, past: "rejected" },
};

export default function EmailAction() {
  // Capture the token ONCE from the URL so StrictMode's double-effect / later
  // history.replaceState can never clobber it.
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("loading"); // loading | ready | working | done | error
  const [result, setResult] = useState(null);
  const started = useRef(false);

  const loadPreview = useCallback(async (t) => {
    try {
      const r = await fetch(`${API}/email-actions/preview?token=${encodeURIComponent(t)}`);
      const x = await r.json();
      if (!r.ok) throw new Error(x?.detail?.message || "This link is invalid.");
      setPreview(x);
      setPhase("ready");
    } catch (e) {
      setError(e.message || "This link is invalid.");
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Clean the token out of the visible URL (one-shot; token is already held in state).
    window.history.replaceState({}, "", window.location.pathname);
    if (!token) { setError("This link is invalid or incomplete."); setPhase("error"); return; }
    loadPreview(token);
  }, [token, loadPreview]);

  async function confirm() {
    setPhase("working");
    try {
      const r = await fetch(`${API}/email-actions/confirm`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const x = await r.json();
      if (!r.ok) throw new Error(x?.detail?.message || "Unable to complete this action.");
      setResult(x);
      setPhase("done");
    } catch (e) {
      setError(e.message || "Unable to complete this action.");
      setPhase("error");
    }
  }

  const meta = preview ? ACTION_META[preview.action] : null;
  const detail = preview?.detail || {};

  return (
    <div data-testid="email-action-page" className="min-h-screen bg-[#f2f6fb] flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-[0_20px_60px_rgba(15,37,87,0.12)] overflow-hidden">
        {/* Brand header */}
        <div className="bg-[#0f2557] px-7 py-5 flex items-center justify-between">
          <span className="text-white font-bold tracking-[0.14em] text-sm">
            Meet<span className="text-[#29abe2]">IQ</span><span className="text-[#c9a227]">360</span>
          </span>
          <span className="text-[11px] text-[#aeb9d1] uppercase tracking-[0.18em]">Secure action</span>
        </div>
        <div className="h-1 bg-gradient-to-r from-[#0f2557] via-[#29abe2] to-[#c9a227]" />

        <div className="p-7">
          {phase === "loading" && (
            <div data-testid="email-action-loading" className="flex items-center gap-2 text-slate-500 py-8 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" /> Checking your link…
            </div>
          )}

          {phase === "error" && (
            <div data-testid="email-action-error" className="text-center py-6">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h1 className="text-xl font-bold text-[#0f2557]">We couldn't open this link</h1>
              <p className="text-sm text-slate-500 mt-2">{error}</p>
              <p className="text-xs text-slate-400 mt-4">If you still need to act on this item, ask the sender for a fresh link.</p>
            </div>
          )}

          {phase === "done" && (
            <div data-testid="email-action-success" className="text-center py-6">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h1 className="text-xl font-bold text-[#0f2557]">All done</h1>
              <p className="text-sm text-slate-500 mt-2">
                This item was {ACTION_META[preview?.action]?.past || "updated"}
                {result?.status ? ` — status is now "${result.status}".` : "."}
              </p>
              <p className="text-xs text-slate-400 mt-4">You can safely close this window.</p>
            </div>
          )}

          {(phase === "ready" || phase === "working") && preview && (
            <div data-testid="email-action-confirm">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                {preview.entity_type === "task" ? "Action item" : "Decision"}
              </div>
              <h1 className="text-xl font-bold text-[#0f2557] mt-1 leading-snug">{detail.title || "(untitled)"}</h1>

              {/* Item details */}
              <div className="mt-4 rounded-2xl bg-[#f2f6fb] p-4 space-y-2 text-sm">
                {preview.entity_type === "task" ? (
                  <>
                    {detail.description && <p className="text-slate-600">{detail.description}</p>}
                    <Row k="Assignee" v={detail.assignee} />
                    <Row k="Due" v={detail.due_date} />
                    <Row k="Priority" v={detail.priority} />
                    <Row k="Current status" v={detail.status} />
                    {detail.meeting_title && <Row k="Meeting" v={detail.meeting_title} />}
                  </>
                ) : (
                  <>
                    {detail.context && <p className="text-slate-600">{detail.context}</p>}
                    {detail.rationale && <p className="text-slate-600"><b>Rationale:</b> {detail.rationale}</p>}
                    <Row k="Impact" v={detail.impact} />
                    <Row k="Owner" v={detail.owner} />
                    <Row k="Current status" v={detail.status} />
                  </>
                )}
              </div>

              {preview.recipient_email && (
                <p className="text-xs text-slate-400 mt-3">Acting as <b>{preview.recipient_email}</b></p>
              )}

              <p className="text-sm text-slate-600 mt-5">
                Please confirm you want to <b>{meta?.verb?.toLowerCase()}</b> this {preview.entity_type}. This link works only once.
              </p>

              <button
                data-testid="email-action-confirm-btn"
                onClick={confirm}
                disabled={phase === "working"}
                style={{ background: meta?.color || "#29abe2" }}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full text-white font-semibold text-sm py-3 disabled:opacity-60 transition-opacity"
              >
                {phase === "working" ? <Loader2 className="w-4 h-4 animate-spin" /> : meta?.Icon && <meta.Icon className="w-4 h-4" />}
                {phase === "working" ? "Working…" : `Confirm — ${meta?.verb}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{k}</span>
      <span className="font-semibold text-[#25304a] text-right">{v || "—"}</span>
    </div>
  );
}
